import React, { useEffect, useRef, useState } from 'react';
import {
  Stack,
  Text,
  Group,
  Button,
  Badge,
  NativeSelect,
  Box,
  Progress,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconShoppingBag,
  IconPrinter,
  IconBaguette,
  IconChevronLeft,
} from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { motion, AnimatePresence } from 'framer-motion';
import { DrawerHeader } from '../UI/DrawerHeader';
import { ResponsiveDrawer, useIsBottomSheet } from '../UI/ResponsiveDrawer';
import { MealPlan, GroceryItem } from '../../types/mealPlan';
import { PantryItem } from '../../types/pantry';
import { GroceryItemCard } from './GroceryItemCard';
import { PantryItemForm } from '../Pantry/PantryItemDrawer';
import { pantryItemCoversRequirement } from '../../utils/unitConversion';
import { useWakeLock } from '../../hooks/useWakeLock';
import { useMediaQuery } from '@mantine/hooks';
import { toggleGroceryListItems } from '../../services/krogerService';

type ShoppingListDrawerProps = {
  opened: boolean;
  onClose: () => void;
  mealPlan: MealPlan;
  planId: string;
  groceryList: GroceryItem[];
  setGroceryList: React.Dispatch<React.SetStateAction<GroceryItem[]>>;
  activeShoppingTab: string | null;
  updateActiveShoppingTab: (tab: string) => void;
  getCategorizedItems: () => {
    produce: GroceryItem[];
    meat: GroceryItem[];
    dairy: GroceryItem[];
    pantry_dry_goods: GroceryItem[];
    spices_seasonings: GroceryItem[];
    frozen: GroceryItem[];
    bakery: GroceryItem[];
    beverages: GroceryItem[];
    other: GroceryItem[];
  };
  getCategoryDisplayName: (category: string) => string;
  handleToggleGroceryItem: (index: number) => void;
  handlePrintShoppingList: () => void;
  // Shared with MealPlanView's toggle handler; see its declaration
  groceryMutationSeqRef: React.MutableRefObject<number>;
  pantryItems: PantryItem[];
  fetchPantryItems: () => void;
  isOwner: boolean;
  user: any;
  navigate: any;
  isKrogerConnected: () => boolean;
  expandedPreferenceItems: Set<string>;
  handleToggleProductPreference: (itemName: string) => void;
  handleSaveProductPreference: (itemName: string, preference: string, scope?: 'meal_plan' | 'ingredient' | 'category' | 'global') => void;
};

export const ShoppingListDrawer = ({
  opened,
  onClose,
  mealPlan,
  planId,
  groceryList,
  setGroceryList,
  activeShoppingTab,
  updateActiveShoppingTab,
  getCategorizedItems,
  getCategoryDisplayName,
  handleToggleGroceryItem,
  handlePrintShoppingList,
  groceryMutationSeqRef,
  pantryItems,
  fetchPantryItems,
  isOwner,
  user,
  navigate,
  isKrogerConnected,
  expandedPreferenceItems,
  handleToggleProductPreference,
  handleSaveProductPreference,
}: ShoppingListDrawerProps) => {
  const { isSupported: wakeLockSupported, requestWakeLock, releaseWakeLock } = useWakeLock();
  const isBottomSheet = useIsBottomSheet();
  // Touch-primary devices tap; mouse/trackpad devices click
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');

  // Pushed view: "Add with quantity" swaps the drawer content for the pantry
  // form (with a back button) instead of stacking a second drawer on top.
  // navDirection drives the slide: 1 = pushing (enter from right), -1 = popping
  const [pantryFormTarget, setPantryFormTarget] = useState<GroceryItem | null>(null);
  const [navDirection, setNavDirection] = useState<1 | -1>(1);
  const pushPantryForm = (item: GroceryItem) => {
    setNavDirection(1);
    setPantryFormTarget(item);
  };
  const popPantryForm = () => {
    setNavDirection(-1);
    setPantryFormTarget(null);
  };

  // Slide + fade for pushed-view navigation, direction-aware like a nav stack
  const viewVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 56 : -56, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -56 : 56, opacity: 0 }),
  };

  // Never keep a pushed view across close/reopen
  useEffect(() => {
    if (!opened) setPantryFormTarget(null);
  }, [opened]);

  // Animate the desktop drawer panel width between views by driving the DOM
  // directly: Mantine manages an inline transition on the content element, so
  // a styles-prop width change lands in the same style flush as the
  // transition and snaps. Append flex-basis to the existing transition, then
  // set the width on the next frame so the change animates.
  // 27.5rem/38.75rem are Mantine's md/lg drawer sizes; the pushed pantry form
  // matches the standalone pantry drawer's narrower md.
  const contentMarkerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isBottomSheet || !opened) return;
    const el = contentMarkerRef.current?.closest('.mantine-Drawer-content') as HTMLElement | null;
    if (!el) return;
    const existing = getComputedStyle(el).transition;
    if (!existing.includes('flex-basis')) {
      el.style.transition = existing + ', flex-basis 0.15s ease-in-out';
    }
    const frame = requestAnimationFrame(() => {
      el.style.flexBasis = pantryFormTarget ? '27.5rem' : '38.75rem';
    });
    return () => cancelAnimationFrame(frame);
  }, [pantryFormTarget, opened, isBottomSheet]);

  // Keep the screen awake while the list is open (in-store shopping)
  useEffect(() => {
    if (opened && wakeLockSupported) {
      requestWakeLock();
    }
    return () => {
      if (wakeLockSupported) {
        releaseWakeLock();
      }
    };
  }, [opened, wakeLockSupported, requestWakeLock, releaseWakeLock]);

  // Global progress counts
  const totalCount = groceryList.length;
  const checkedCount = groceryList.filter(item => item.alreadyHave).length;
  const progressPercent = totalCount > 0 ? (checkedCount / totalCount) * 100 : 0;

  // Build category tabs
  const categorizedItems = getCategorizedItems();
  const sortedGroceryList = [...groceryList].sort((a, b) =>
    a.itemName.toLowerCase().localeCompare(b.itemName.toLowerCase())
  );
  const availableCategories = [
    ...(groceryList.length > 0
      ? [{ category: 'all', items: sortedGroceryList, count: groceryList.length }]
      : []),
    ...Object.entries(categorizedItems)
      .filter(([_, items]) => items.length > 0)
      .map(([category, items]) => ({ category, items, count: items.length })),
  ];

  // Auto-fallback if active tab is invalid
  const validTab = availableCategories.some(cat => cat.category === activeShoppingTab);
  const effectiveTab = validTab ? activeShoppingTab : (availableCategories[0]?.category || 'all');

  // Get active items split by checked state
  const activeCategory = availableCategories.find(cat => cat.category === effectiveTab);
  const activeItems = activeCategory?.items || [];
  const uncheckedItems = activeItems.filter(item => !item.alreadyHave);

  const findOriginalIndex = (item: GroceryItem) =>
    groceryList.findIndex(
      orig => orig.itemName === item.itemName && orig.totalQuantity === item.totalQuantity
    );

  const handleToggleAll = async () => {
    if (!user || !activeCategory) return;

    // If there are unchecked items, check all. Otherwise, uncheck all.
    const targetState = uncheckedItems.length > 0;

    // Only toggle items that need to change state to avoid confusing behavior
    const itemsToToggle = activeCategory.items
      .filter(item => item.alreadyHave !== targetState)
      .map(item => item.itemName);

    if (itemsToToggle.length === 0) return;

    // Optimistic update - immediately update the state
    const seq = ++groceryMutationSeqRef.current;
    const toggledNames = new Set(itemsToToggle);
    setGroceryList(prev => prev.map(item =>
      toggledNames.has(item.itemName) ? { ...item, alreadyHave: targetState } : item
    ));

    // Revert only the items this action changed; a full-snapshot rollback
    // would also undo unrelated toggles made while the request was in flight
    const revertToggledItems = () =>
      setGroceryList(prev => prev.map(item =>
        toggledNames.has(item.itemName) ? { ...item, alreadyHave: !targetState } : item
      ));

    try {
      // Persist the changes to the backend - only for items that need to change
      const token = await user.getIdToken();
      const result = await toggleGroceryListItems(planId, itemsToToggle, token);

      if (result.success && result.groceryList) {
        // Apply the server snapshot only if no newer mutation started while
        // this request was in flight (it would clobber those toggles)
        if (groceryMutationSeqRef.current === seq) {
          setGroceryList(result.groceryList);
        }
      } else {
        revertToggledItems();
      }
    } catch (error) {
      console.error('Error toggling all grocery items:', error);
      revertToggledItems();
    }
  };

  // After the pushed pantry form saves: if the added quantity covers what
  // the plan needs, check the grocery item off (mirrors GroceryItemActions'
  // standalone behavior), then refresh pantry badges
  const handlePantryFormSave = (savedItem: PantryItem) => {
    const target = pantryFormTarget;
    if (target && target.totalQuantity && target.unit &&
        pantryItemCoversRequirement(savedItem, target.totalQuantity, target.unit)) {
      const index = findOriginalIndex(target);
      const current = index !== -1 ? groceryList[index] : undefined;
      if (current && !current.alreadyHave) {
        handleToggleGroceryItem(index);
      }
    }
    fetchPantryItems();
  };

  const handlePantryFormDuplicate = (existingItem: PantryItem) => {
    modals.openConfirmModal({
      title: 'Item already in pantry',
      children: (
        <Text size="sm">
          <strong>{existingItem.displayName}</strong> is already in your pantry.
          <br /><br />
          Current quantity: {existingItem.quantity ? `${existingItem.quantity} ${existingItem.unit}` : 'Staple item'}
          <br /><br />
          Would you like to edit the existing item?
        </Text>
      ),
      labels: { confirm: 'Edit existing item', cancel: 'Cancel' },
      confirmProps: { color: 'orange' },
      onConfirm: () => {
        fetchPantryItems();
        notifications.show({
          title: 'Navigate to pantry',
          message: 'Go to my pantry to edit this item.',
          color: 'blue',
          autoClose: 4000,
        });
      },
    });
  };

  const renderItemCard = (item: GroceryItem) => (
    <GroceryItemCard
      key={item.itemName}
      item={item}
      mealPlan={mealPlan}
      planId={planId}
      pantryItems={pantryItems}
      fetchPantryItems={fetchPantryItems}
      setGroceryList={setGroceryList}
      isOwner={isOwner}
      navigate={navigate}
      krogerConnected={isKrogerConnected()}
      onToggle={() => handleToggleGroceryItem(findOriginalIndex(item))}
      preferenceExpanded={expandedPreferenceItems.has(item.itemName)}
      onAddWithQuantity={() => pushPantryForm(item)}
      handleToggleProductPreference={handleToggleProductPreference}
      handleSaveProductPreference={handleSaveProductPreference}
    />
  );

  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={onClose}
      size="lg"
      mobileSize="95%"
      // Keep the sheet a fixed height on mobile. Shrinking to fit a small
      // category slid the sheet down under the user's finger, and moving the
      // anchor after a dropdown had been positioned left menus hanging off
      // the bottom of the screen with no chance to flip back up.
      mobileShrinkToContent={false}
      styles={{
        // Absolutely position the close button so the title block (progress,
        // category chips, print) spans the full drawer width instead of
        // stopping at the close button's flex column
        header: { position: 'relative', alignItems: 'flex-start' },
        title: { flex: 1 },
        close: { position: 'absolute', top: 16, right: 16 },
      }}
      title={
        <AnimatePresence mode="popLayout" initial={false}>
        <motion.div
          key={pantryFormTarget ? 'form-header' : 'list-header'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
        {pantryFormTarget ? (
          <Group gap="xs" wrap="nowrap">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              aria-label="Back to shopping list"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={popPantryForm}
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <Box style={{ flex: 1 }}>
              <DrawerHeader
                icon={<IconBaguette size={18} />}
                title="Add pantry item"
                subtitle={pantryFormTarget.itemName}
              />
            </Box>
          </Group>
        ) : (
        <>
          <DrawerHeader
            icon={<IconShoppingBag size={18} />}
            title="Shopping list"
            subtitle={`${checkedCount} of ${totalCount} checked off`}
          />
          {/* Progress bar + category selector. Stop pointer events here so
              pressing the select doesn't start the sheet's swipe-to-close
              drag (the whole header is the drag handle). */}
          <Box pt="sm" onPointerDown={(e) => e.stopPropagation()}>
            <Progress value={progressPercent} color="orange" size="sm" mb="sm" radius="xl" />
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <Box style={{ flex: 1, minWidth: 0 }}>
                {isBottomSheet ? (
                  // The platform's native picker: a floating dropdown portaled
                  // to the body gets mispositioned past the end of the
                  // document (the sheet is fixed and transformed), which
                  // grows the mobile layout viewport and shoves the whole
                  // sheet down while the dropdown floats.
                  <NativeSelect
                    value={effectiveTab ?? undefined}
                    onChange={(e) => updateActiveShoppingTab(e.currentTarget.value)}
                    data={availableCategories.map(({ category, items }) => {
                      const unchecked = items.filter(i => !i.alreadyHave).length;
                      return {
                        value: category,
                        label: unchecked > 0
                          ? `${getCategoryDisplayName(category)} (${unchecked} needed)`
                          : getCategoryDisplayName(category),
                      };
                    })}
                    size="sm"
                    styles={{ input: { fontSize: '16px' } }}
                  />
                ) : (
                  // Wide screens have room for category chips with counts
                  <Group gap="xs" style={{ flexWrap: 'wrap' }}>
                    {availableCategories.map(({ category, items }) => {
                      const uncheckedCount = items.filter(item => !item.alreadyHave).length;
                      const isActive = effectiveTab === category;
                      return (
                        <Button
                          key={category}
                          variant={isActive ? "filled" : "light"}
                          size="compact-sm"
                          onClick={() => updateActiveShoppingTab(category)}
                          style={{ minWidth: 'auto', fontSize: '0.75rem' }}
                          rightSection={uncheckedCount > 0 ? (
                            <Badge
                              size="xs"
                              variant="filled"
                              color={isActive ? "orange.1" : "orange"}
                              style={{
                                marginLeft: '4px',
                                fontSize: '10px',
                                height: '16px',
                                minWidth: '16px',
                                color: isActive ? 'var(--mantine-color-orange-8)' : 'white'
                              }}
                            >
                              {uncheckedCount}
                            </Badge>
                          ) : null}
                        >
                          {getCategoryDisplayName(category)}
                        </Button>
                      );
                    })}
                  </Group>
                )}
              </Box>
              <Tooltip label="Print shopping list" position="left" withArrow>
                <ActionIcon
                  variant="light"
                  color="gray"
                  size={isBottomSheet ? 36 : 28}
                  onClick={handlePrintShoppingList}
                  style={{ flexShrink: 0 }}
                >
                  <IconPrinter size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Box>
        </>
        )}
        </motion.div>
        </AnimatePresence>
      }
    >
      <div ref={contentMarkerRef} style={{ display: 'none' }} />
      <AnimatePresence mode="popLayout" initial={false} custom={navDirection}>
      <motion.div
        key={pantryFormTarget ? 'form-view' : 'list-view'}
        custom={navDirection}
        variants={viewVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
      {pantryFormTarget ? (
        <PantryItemForm
          prefillName={pantryFormTarget.itemName}
          prefillQuantity={pantryFormTarget.totalQuantity}
          prefillUnit={pantryFormTarget.unit}
          prefillCategory={pantryFormTarget.category}
          prefillType="ingredient"
          onSave={handlePantryFormSave}
          onDuplicateFound={handlePantryFormDuplicate}
          onDone={popPantryForm}
          resetSignal={pantryFormTarget}
        />
      ) : (
        <Stack gap="sm" pb="md">
          {/* Helper text with bulk toggle */}
          {isOwner && activeItems.length > 0 && (
            <Text size="xs" c="dimmed" px="xs">
              {isCoarsePointer ? 'Tap' : 'Click'} items you already have to check them off your list
              {' '}
              <Text component="span" size="xs" style={{ whiteSpace: 'nowrap' }}>
                (
                <Text
                  component="span"
                  size="xs"
                  c="orange.6"
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={handleToggleAll}
                >
                  {uncheckedItems.length > 0 ? 'check all' : 'uncheck all'}
                </Text>
                )
              </Text>
            </Text>
          )}

          {/* Items stay in place when checked off (dimmed + struck through);
              moving them on tap was disorienting */}
          {activeItems.map(renderItemCard)}
        </Stack>
      )}
      </motion.div>
      </AnimatePresence>
    </ResponsiveDrawer>
  );
};
